<template>
  <div class="flex flex-col w-full">
    <h2 class="text-2xl font-bold mb-4">Location Finder - Philippines</h2>

    <div class="mb-4">
      <div class="flex gap-2 items-start">
        <div class="flex-1">
          <span class="p-float-label w-full">
            <AutoComplete
              id="search-input"
              v-model="searchQuery"
              :suggestions="searchSuggestions"
              @complete="searchAutocomplete"
              @item-select="handleSuggestionSelect"
              @keyup.enter="searchLocation"
              placeholder="Search for a location in the Philippines"
              class="w-full"
              forceSelection
              dropdown
              field="displayName"
              optionLabel="displayName"
            >
              <template #item="slotProps">
                <div class="py-1 px-2 text-sm">
                  {{ slotProps.item.displayName }}
                </div>
              </template>
            </AutoComplete>
          </span>
          <p class="text-xs text-gray-500 mt-1">Search is restricted to Philippines locations only</p>
        </div>
        <Button
          v-if="selectedLocation"
          label="Confirm Address"
          icon="pi pi-check"
          severity="success"
          @click="confirmAddress"
          class="whitespace-nowrap"
        />
      </div>
    </div>

    <div class="mb-4">
      <div ref="mapContainer" class="w-full h-96 border border-gray-300 rounded"></div>
    </div>

    <div v-if="selectedLocation" class="bg-gray-100 p-4 rounded mb-4">
      <h3 class="font-bold text-lg mb-2">Selected Location</h3>
      <div class="grid grid-cols-2 gap-2">
        <div class="font-medium">Name:</div>
        <div>{{ selectedLocation.name || 'Unknown' }}</div>

        <div class="font-medium">Address:</div>
        <div>{{ selectedLocation.address || 'Unknown' }}</div>

        <div class="font-medium">City:</div>
        <div>{{ selectedLocation.city || 'Unknown' }}</div>

        <div class="font-medium">Zip Code:</div>
        <div>{{ selectedLocation.zipCode || 'Unknown' }}</div>

        <div class="font-medium">Country:</div>
        <div>{{ selectedLocation.country || 'Unknown' }}</div>

        <div class="font-medium">Coordinates:</div>
        <div>{{ selectedLocation.lat.toFixed(6) }}, {{ selectedLocation.lng.toFixed(6) }}</div>

        <div class="font-medium">Type:</div>
        <div>{{ selectedLocation.type || 'Unknown' }}</div>
      </div>
    </div>

    <Toast />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import AutoComplete from 'primevue/autocomplete';
import Toast from 'primevue/toast';

// Import Leaflet
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers (important!)
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Fix default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

// Philippines boundaries (approximate)
const PHILIPPINES_BOUNDS = {
  north: 21.3,
  south: 4.5,
  west: 116.0,
  east: 127.0,
  center: [12.8797, 121.7740] // Center of Philippines (Manila area)
};

// References
const mapContainer = ref(null);
const searchQuery = ref('');
const searchSuggestions = ref([]);
const selectedLocation = ref(null);
const map = ref(null);
const marker = ref(null);
const toast = useToast();
const searchTimeout = ref(null);

const emit = defineEmits(['handle-address', 'address-confirmed']);

// Check if coordinates are within Philippines
const isWithinPhilippines = (lat, lng) => {
  return (
    lat >= PHILIPPINES_BOUNDS.south &&
    lat <= PHILIPPINES_BOUNDS.north &&
    lng >= PHILIPPINES_BOUNDS.west &&
    lng <= PHILIPPINES_BOUNDS.east
  );
};

// Confirm address button handler
const confirmAddress = () => {
  if (selectedLocation.value) {
    toast.add({
      severity: 'success',
      summary: 'Address Confirmed',
      detail: 'Location has been confirmed successfully',
      life: 3000
    });

    // Emit both events for backward compatibility
    emit('handle-address', selectedLocation.value);
    emit('address-confirmed', selectedLocation.value);
  }
};

// Leaflet map initialization
onMounted(() => {
  initMap();
});

onUnmounted(() => {
  if (map.value) {
    map.value.remove();
  }
});

const initMap = () => {
  try {
    // Initialize the map with Philippines center
    map.value = L.map(mapContainer.value).setView(PHILIPPINES_BOUNDS.center, 6);

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map.value);

    // Set max bounds to restrict panning to Philippines area
    const bounds = L.latLngBounds(
      [PHILIPPINES_BOUNDS.south, PHILIPPINES_BOUNDS.west],
      [PHILIPPINES_BOUNDS.north, PHILIPPINES_BOUNDS.east]
    );
    map.value.setMaxBounds(bounds);
    map.value.setMinZoom(6);

    // Add click event to the map
    map.value.on('click', handleMapClick);

    // Try to get user's current location (if within Philippines)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (isWithinPhilippines(latitude, longitude)) {
            map.value.setView([latitude, longitude], 13);
            toast.add({
              severity: 'success',
              summary: 'Location Found',
              detail: 'Using your current location',
              life: 3000
            });
          } else {
            toast.add({
              severity: 'info',
              summary: 'Info',
              detail: 'Your location is outside Philippines. Showing default view.',
              life: 3000
            });
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  } catch (error) {
    console.error('Error initializing map:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to initialize map',
      life: 3000
    });
  }
};

const handleMapClick = async (e) => {
  const { lat, lng } = e.latlng;

  // Check if click is within Philippines
  if (!isWithinPhilippines(lat, lng)) {
    toast.add({
      severity: 'warn',
      summary: 'Invalid Location',
      detail: 'Please select a location within the Philippines',
      life: 3000
    });
    return;
  }

  // Update or create marker
  if (marker.value) {
    marker.value.setLatLng([lat, lng]);
  } else {
    marker.value = L.marker([lat, lng]).addTo(map.value);
  }

  try {
    // Get location details using reverse geocoding
    const locationInfo = await reverseGeocode(lat, lng);

    // Verify the location is in Philippines
    if (locationInfo.country && !locationInfo.country.toLowerCase().includes('philippines')) {
      toast.add({
        severity: 'warn',
        summary: 'Invalid Location',
        detail: 'Selected location is outside the Philippines',
        life: 3000
      });

      // Remove marker
      if (marker.value) {
        map.value.removeLayer(marker.value);
        marker.value = null;
      }
      selectedLocation.value = null;
      return;
    }

    selectedLocation.value = {
      lat,
      lng,
      ...locationInfo
    };
  } catch (error) {
    console.error('Error getting location details:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to get location details',
      life: 3000
    });

    // Set basic location info without details
    selectedLocation.value = {
      lat,
      lng,
      name: 'Unknown location',
      address: 'Address not available',
      type: 'Unknown',
      country: 'Philippines'
    };
  }
};

// Handle autocomplete suggestions
const searchAutocomplete = async (event) => {
  // Clear any existing timeout to prevent multiple API calls
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  const query = event.query.trim();

  // Don't search if the query is too short
  if (query.length < 2) {
    searchSuggestions.value = [];
    return;
  }

  // Debounce the API call to avoid too many requests
  searchTimeout.value = setTimeout(async () => {
    try {
      // Using Nominatim API for geocoding autocomplete - restricted to Philippines
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `format=json&` +
        `q=${encodeURIComponent(query)}, Philippines&` +
        `countrycodes=ph&` +
        `limit=5&` +
        `bounded=1&` +
        `viewbox=${PHILIPPINES_BOUNDS.west},${PHILIPPINES_BOUNDS.north},${PHILIPPINES_BOUNDS.east},${PHILIPPINES_BOUNDS.south}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        // Filter results to ensure they're within Philippines bounds
        const filteredResults = data.filter(item => {
          const lat = parseFloat(item.lat);
          const lng = parseFloat(item.lon);
          return isWithinPhilippines(lat, lng);
        });

        searchSuggestions.value = filteredResults.map(item => ({
          displayName: item.display_name,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon)
        }));

        if (filteredResults.length === 0) {
          toast.add({
            severity: 'info',
            summary: 'No Results',
            detail: 'No locations found in the Philippines matching your search',
            life: 3000
          });
        }
      } else {
        searchSuggestions.value = [];
        toast.add({
          severity: 'info',
          summary: 'No Results',
          detail: 'No locations found in the Philippines',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
      searchSuggestions.value = [];
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to get suggestions',
        life: 3000
      });
    }
  }, 300); // 300ms debounce
};

// Handle when user selects an item from the suggestions
const handleSuggestionSelect = (event) => {
  const { lat, lng, displayName } = event.value;

  // Double-check if location is within Philippines
  if (!isWithinPhilippines(lat, lng)) {
    toast.add({
      severity: 'warn',
      summary: 'Invalid Location',
      detail: 'Selected location is outside the Philippines',
      life: 3000
    });
    searchQuery.value = '';
    return;
  }

  // Update map view
  map.value.setView([lat, lng], 16);

  // Update or create marker
  if (marker.value) {
    marker.value.setLatLng([lat, lng]);
  } else {
    marker.value = L.marker([lat, lng]).addTo(map.value);
  }

  // Get full location details using reverse geocoding
  reverseGeocode(lat, lng).then(locationInfo => {
    // Verify the location is in Philippines
    if (locationInfo.country && !locationInfo.country.toLowerCase().includes('philippines')) {
      toast.add({
        severity: 'warn',
        summary: 'Invalid Location',
        detail: 'Selected location is outside the Philippines',
        life: 3000
      });

      // Remove marker
      if (marker.value) {
        map.value.removeLayer(marker.value);
        marker.value = null;
      }
      selectedLocation.value = null;
      searchQuery.value = '';
      return;
    }

    // Update selected location
    selectedLocation.value = {
      lat,
      lng,
      name: displayName.split(',')[0],
      address: displayName,
      ...locationInfo
    };

    // Reset searchQuery to the full address
    if (typeof searchQuery.value !== 'string') {
      searchQuery.value = displayName;
    }
  }).catch(error => {
    console.error('Error getting detailed location:', error);

    // Fallback with basic info
    selectedLocation.value = {
      lat,
      lng,
      name: displayName.split(',')[0],
      address: displayName,
      city: 'Unknown',
      zipCode: 'Unknown',
      country: 'Philippines',
      type: 'Search result'
    };

    // Reset searchQuery to the full address
    if (typeof searchQuery.value !== 'string') {
      searchQuery.value = displayName;
    }
  });
};

const searchLocation = async () => {
  // If the query is a string (not an object from autocomplete)
  if (!searchQuery.value || typeof searchQuery.value === 'string') {
    const query = typeof searchQuery.value === 'string' ? searchQuery.value.trim() : '';

    if (!query) {
      toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter a search query',
        life: 3000
      });
      return;
    }

    try {
      const result = await geocode(query);
      if (result) {
        const { lat, lng, displayName } = result;

        // Check if location is within Philippines
        if (!isWithinPhilippines(lat, lng)) {
          toast.add({
            severity: 'warn',
            summary: 'Invalid Location',
            detail: 'Location found is outside the Philippines',
            life: 3000
          });
          return;
        }

        // Update map view
        map.value.setView([lat, lng], 16);

        // Update or create marker
        if (marker.value) {
          marker.value.setLatLng([lat, lng]);
        } else {
          marker.value = L.marker([lat, lng]).addTo(map.value);
        }

        // Get full location details using reverse geocoding
        const locationInfo = await reverseGeocode(lat, lng);

        // Verify the location is in Philippines
        if (locationInfo.country && !locationInfo.country.toLowerCase().includes('philippines')) {
          toast.add({
            severity: 'warn',
            summary: 'Invalid Location',
            detail: 'Selected location is outside the Philippines',
            life: 3000
          });

          // Remove marker
          if (marker.value) {
            map.value.removeLayer(marker.value);
            marker.value = null;
          }
          selectedLocation.value = null;
          return;
        }

        // Update selected location
        selectedLocation.value = {
          lat,
          lng,
          name: displayName.split(',')[0],
          address: displayName,
          ...locationInfo
        };

        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Location found',
          life: 3000
        });
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No results found in the Philippines',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error searching location:', error);
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to search location',
        life: 3000
      });
    }
  }
};

// Function to perform geocoding (address to coordinates) - restricted to Philippines
const geocode = async (query) => {
  try {
    // Using Nominatim API for geocoding (OpenStreetMap) - restricted to Philippines
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `format=json&` +
      `q=${encodeURIComponent(query)}, Philippines&` +
      `countrycodes=ph&` +
      `bounded=1&` +
      `viewbox=${PHILIPPINES_BOUNDS.west},${PHILIPPINES_BOUNDS.north},${PHILIPPINES_BOUNDS.east},${PHILIPPINES_BOUNDS.south}`
    );
    const data = await response.json();

    if (data && data.length > 0) {
      // Find first result within Philippines bounds
      for (const result of data) {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        if (isWithinPhilippines(lat, lng)) {
          return {
            lat,
            lng,
            displayName: result.display_name
          };
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
};

// Function to perform reverse geocoding (coordinates to address)
const reverseGeocode = async (lat, lng) => {
  try {
    // Using Nominatim API for reverse geocoding (OpenStreetMap)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();

    if (data && data.display_name) {
      // Extract establishment type if available
      let type = 'Unknown';
      if (data.address) {
        if (data.address.amenity) type = data.address.amenity;
        else if (data.address.shop) type = data.address.shop;
        else if (data.address.tourism) type = data.address.tourism;
        else if (data.address.building) type = data.address.building;
      }

      // Extract address components
      const city = data.address ? (data.address.city || data.address.town || data.address.village || data.address.municipality || data.address.hamlet || 'Unknown') : 'Unknown';
      const zipCode = data.address ? (data.address.postcode || 'Unknown') : 'Unknown';
      const country = data.address ? (data.address.country || 'Philippines') : 'Philippines';

      return {
        name: data.name || data.display_name.split(',')[0],
        address: data.display_name,
        city,
        zipCode,
        country,
        type
      };
    }

    return {
      name: 'Unknown location',
      address: 'Address not available',
      city: 'Unknown',
      zipCode: 'Unknown',
      country: 'Philippines',
      type: 'Unknown'
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};
</script>

<style scoped>
/* Add any custom styles here if needed */
.p-autocomplete {
  width: 100%;
}
</style>
